import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import { type COIDocument, setCOIAutoRegenerate } from '../../../../models/coi'

/**
 * Whether a policy change rebuilds this certificate. Off pins it to the document
 * on file, so uploading a policy cannot overwrite a carrier-supplied or
 * hand-corrected PDF — the regenerate button still rebuilds it on demand.
 *
 * Staff-only, like the upload and regenerate actions it sits beside: the caller
 * gates on that.
 */
export function COIAutoRegenerateSwitch({
  document,
  orgId,
  onChanged,
}: {
  document: COIDocument
  orgId?: string
  onChanged?: () => void
}) {
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  // A backend predating the field says nothing, which means regeneration is on.
  const autoRegenerate = document.autoRegenerate ?? true

  async function onCheckedChange(checked: boolean) {
    setSaving(true)
    try {
      await setCOIAutoRegenerate(document.id, checked, orgId)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_DOCUMENTS_CURRENT] })
      onChanged?.()
      toast.success(
        checked
          ? 'This certificate will be regenerated when a policy changes'
          : 'This certificate will no longer be regenerated automatically'
      )
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Switch
            checked={autoRegenerate}
            disabled={saving}
            aria-label="Regenerate this certificate automatically"
            onCheckedChange={onCheckedChange}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {autoRegenerate
          ? 'Rebuilt automatically whenever a policy document changes'
          : 'Pinned to the document on file — policy changes will not rebuild it'}
      </TooltipContent>
    </Tooltip>
  )
}
